import { Children, FC, ReactNode, useLayoutEffect, useRef } from 'react';
import {
  BlueprintPlaceholder,
  BlueprintUnitState,
} from 'shared/ui/blueprint-placeholder/blueprint-placeholder';
import cls from './unit-slot.module.scss';

export type UnitState = BlueprintUnitState | 'ready';

interface UnitSlotProps {
  unitState: UnitState;
  name: string;
  minHeight?: number;
  className?: string;
  children?: ReactNode;
  startedAt?: string | null;
  expectedSeconds?: number | null;
}

const HEIGHT_TRAVEL_MS = 300;

export const UnitSlot: FC<UnitSlotProps> = ({
  unitState,
  name,
  minHeight,
  className = '',
  children,
  startedAt = null,
  expectedSeconds = null,
}) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasContent = Children.count(children) > 0;
  const isReady = unitState === 'ready' && hasContent;
  const placeholderState: BlueprintUnitState =
    unitState === 'queued' ? 'queued' : 'in-progress';

  const wasReadyRef = useRef(isReady);
  const previousHeightRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  useLayoutEffect(() => {
    const node = slotRef.current;

    if (!node) {
      return;
    }

    if (isReady === wasReadyRef.current) {
      if (!isAnimatingRef.current) {
        previousHeightRef.current = node.offsetHeight;
      }
      return;
    }

    wasReadyRef.current = isReady;
    const from = previousHeightRef.current;
    const to = contentRef.current?.offsetHeight ?? node.scrollHeight;
    previousHeightRef.current = to;

    if (from === null || from === to) {
      return;
    }

    isAnimatingRef.current = true;
    node.style.height = `${from}px`;
    void node.offsetHeight;
    node.style.transition = `height ${HEIGHT_TRAVEL_MS}ms cubic-bezier(0.2, 0, 0, 1)`;
    node.style.height = `${to}px`;

    const handleEnd = () => {
      node.style.height = '';
      node.style.transition = '';
      isAnimatingRef.current = false;
      node.removeEventListener('transitionend', handleEnd);
    };

    node.addEventListener('transitionend', handleEnd);
  });

  return (
    <div
      ref={slotRef}
      className={`${cls.unitSlot} ${className}`}
      data-state={unitState}
      style={!isReady && minHeight ? { minHeight } : undefined}
    >
      {isReady ? (
        <div ref={contentRef} className={cls.content}>
          {children}
        </div>
      ) : (
        <BlueprintPlaceholder
          unitState={placeholderState}
          name={name}
          startedAt={startedAt}
          expectedSeconds={expectedSeconds}
        />
      )}
    </div>
  );
};
